import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';

interface PaymentData {
  amount: number;
  tokenAmount: number;
  phase: number;
  pricePerToken: string;
}

export const XenditPaymentPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const paymentData = (location.state as PaymentData) || {
    amount: 0,
    tokenAmount: 0,
    phase: 1,
    pricePerToken: '0.05'
  };

  const [paymentMethod, setPaymentMethod] = useState<string>('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);

  const paymentMethods = [
    { id: 'bank_transfer', name: 'Bank Transfer', icon: 'university', methods: ['BCA', 'BNI', 'Mandiri', 'BRI', 'Permata'] },
    { id: 'e_wallet', name: 'E-Wallet', icon: 'wallet', methods: ['OVO', 'DANA', 'GoPay', 'LinkAja', 'ShopeePay'] },
    { id: 'retail', name: 'Retail Store', icon: 'store', methods: ['Alfamart', 'Indomaret'] },
    { id: 'credit_card', name: 'Credit/Debit Card', icon: 'credit-card', methods: ['Visa', 'Mastercard', 'JCB'] },
  ];

  const handlePayment = async () => {
    if (!paymentMethod || !email || !phone) {
      alert('Please fill in all required fields');
      return;
    }

    setLoading(true);

    // Simulate Xendit API call
    setTimeout(() => {
      setLoading(false);
      // Simulate success redirect to payment instruction page
      navigate('/payment/instructions', {
        state: {
          ...paymentData,
          paymentMethod,
          email,
          phone,
          invoiceId: `INV-${Date.now()}`,
          virtualAccount: generateVirtualAccount(),
          expiryTime: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
        }
      });
    }, 2000);
  };

  const generateVirtualAccount = () => {
    const prefix = paymentMethod === 'BCA' ? '70012' :
                   paymentMethod === 'BNI' ? '8808' :
                   paymentMethod === 'Mandiri' ? '88808' : '8808';
    return prefix + Math.floor(Math.random() * 10000000000).toString().padStart(10, '0');
  };

  return (
    <div className="min-h-screen bg-bg-primary py-20">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-12">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-text-secondary hover:text-gold transition-colors mb-6"
          >
            <i className="fas fa-arrow-left"></i>
            Back to Presale
          </button>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">Complete Your Purchase</h1>
          <p className="text-text-secondary">Secure payment powered by Xendit</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Payment Form */}
          <div className="lg:col-span-2">
            <Card className="p-6 mb-6">
              <h2 className="text-xl font-semibold text-white mb-6">Customer Information</h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="w-full px-4 py-3 bg-surface border border-border rounded-lg text-white placeholder-text-tertiary focus:outline-none focus:border-gold transition-colors"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+62 812 3456 7890"
                    className="w-full px-4 py-3 bg-surface border border-border rounded-lg text-white placeholder-text-tertiary focus:outline-none focus:border-gold transition-colors"
                    required
                  />
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <h2 className="text-xl font-semibold text-white mb-6">Select Payment Method</h2>

              <div className="space-y-4">
                {paymentMethods.map((method) => (
                  <div key={method.id} className="border border-border rounded-lg overflow-hidden">
                    <button
                      onClick={() => setPaymentMethod(paymentMethod === method.id ? '' : method.id)}
                      className="w-full flex items-center justify-between p-4 hover:bg-surface transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <i className={`fas fa-${method.icon} text-gold text-xl`}></i>
                        <span className="font-medium text-white">{method.name}</span>
                      </div>
                      <i className={`fas fa-chevron-${paymentMethod === method.id ? 'up' : 'down'} text-text-secondary`}></i>
                    </button>

                    {paymentMethod === method.id && (
                      <div className="p-4 bg-surface border-t border-border">
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                          {method.methods.map((subMethod) => (
                            <button
                              key={subMethod}
                              onClick={() => setPaymentMethod(subMethod)}
                              className={`p-3 border rounded-lg text-sm font-medium transition-all ${
                                paymentMethod === subMethod
                                  ? 'border-gold bg-gold/10 text-gold'
                                  : 'border-border text-text-secondary hover:border-gold/50'
                              }`}
                            >
                              {subMethod}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <Card className="p-6 sticky top-24">
              <h2 className="text-xl font-semibold text-white mb-6">Order Summary</h2>

              <div className="space-y-4 mb-6">
                <div className="flex justify-between text-sm">
                  <span className="text-text-secondary">Phase</span>
                  <span className="text-white font-medium">Phase {paymentData.phase}</span>
                </div>

                <div className="flex justify-between text-sm">
                  <span className="text-text-secondary">Token Amount</span>
                  <span className="text-white font-medium">{paymentData.tokenAmount.toLocaleString()} IDNG</span>
                </div>

                <div className="flex justify-between text-sm">
                  <span className="text-text-secondary">Price per Token</span>
                  <span className="text-white font-medium">${paymentData.pricePerToken}</span>
                </div>

                <div className="border-t border-border pt-4">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-text-secondary">Subtotal (USD)</span>
                    <span className="text-white">${paymentData.amount.toFixed(2)}</span>
                  </div>

                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-text-secondary">Exchange Rate</span>
                    <span className="text-white">1 USD = 15,800 IDR</span>
                  </div>
                </div>

                <div className="border-t border-border pt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold text-white">Total (IDR)</span>
                    <span className="text-2xl font-bold gradient-text">
                      {(paymentData.amount * 15800).toLocaleString('id-ID', {
                        style: 'currency',
                        currency: 'IDR',
                        minimumFractionDigits: 0,
                      })}
                    </span>
                  </div>
                </div>
              </div>

              <Button
                variant="primary"
                className="w-full"
                onClick={handlePayment}
                disabled={!paymentMethod || !email || !phone || loading}
              >
                {loading ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-2"></i>
                    Processing...
                  </>
                ) : (
                  <>
                    <i className="fas fa-lock mr-2"></i>
                    Complete Payment
                  </>
                )}
              </Button>

              <div className="mt-4 flex items-center justify-center gap-2 text-xs text-text-tertiary">
                <i className="fas fa-shield-alt text-gold"></i>
                <span>Secured by Xendit</span>
              </div>
            </Card>
          </div>
        </div>

        {/* Trust Indicators */}
        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="text-center">
            <i className="fas fa-lock text-3xl text-gold mb-2"></i>
            <p className="text-sm text-text-secondary">Secure Payment</p>
          </div>
          <div className="text-center">
            <i className="fas fa-shield-alt text-3xl text-gold mb-2"></i>
            <p className="text-sm text-text-secondary">PCI DSS Certified</p>
          </div>
          <div className="text-center">
            <i className="fas fa-check-circle text-3xl text-gold mb-2"></i>
            <p className="text-sm text-text-secondary">Instant Confirmation</p>
          </div>
          <div className="text-center">
            <i className="fas fa-headset text-3xl text-gold mb-2"></i>
            <p className="text-sm text-text-secondary">24/7 Support</p>
          </div>
        </div>
      </div>
    </div>
  );
};
